import { useState } from 'react';
import { z } from 'zod';
import { request, useMutation, useQuery, useQueryClient } from '@applyboard/ui-utils';
import { VasError } from '../common/errors/VasError';
import { getAccessToken } from '../common/utils/accessToken';
import { refetchType } from '.';

export const V2_LOAN_APPLICATION_URL = '/api/loans/v2/applications';
export const V2_LOAN_APPLICATION_SUBMISSION_URL = '/api/loans/v2/applications/submission';
export const V2_LEAD_LOAN_URL = '/api/loans/v2/applications/lead';
export const V2_CHECK_DUPLICATE_LOAN_APPLICATION_URL = '/api/loans/v2/applications/check_duplicate';
export const V2_SEND_FOLLOW_UP_EMAIL_URL = (applicationId: string) =>
  `/api/loans/v2/applications/lead/${applicationId}/email_follow_up`;

export type LoansErrorBody = {
  message: string | string[];
  error: string;
  statusCode: number;
  errors?: JsonApiError[]; // optional so the type is backwards compatible with other instances where it's created
};

export type JsonApiError = {
  detail: string;
  status: string;
  title: string;
};

export class LoansError extends Error {
  public readonly errorBody: LoansErrorBody;

  constructor(body: LoansErrorBody, message: string) {
    super(message);
    this.errorBody = body;
  }
}

const studentPersonalInformationReqSchema = z.object({
  given_name: z.string().trim(),
  last_name: z.string().trim().optional(),
  email_address: z.string().trim().email(),
  phone_number: z.string().trim(),
  whatsapp_number: z.string().trim().optional(),
  nationality: z.string().trim().length(2),
});

const loanDocumentSchema = z
  .array(
    z.object({
      type: z.string(),
      file_name: z.string(),
      content: z.string(),
    }),
  )
  .optional();

const destinationInformationLeadReqSchema = z.object({
  destination_country: z.string().trim().length(2),
  school_name: z.string().trim(),
  program_name: z.string().trim().optional(),
  field_of_study: z.string().trim().optional(),
});

const destinationInformationReqSchema = z.object({
  destination_country: z.string().trim().length(2),
  field_of_study: z.string().trim(),
  school_name: z.string().trim(),
  program_name: z.string().trim().optional(),
  expected_arrival_date: z.string().trim(),
  expected_graduation_date: z.string().trim(),
});

const englishExamResultReqSchema = z.object({
  listening: z.number().optional(),
  writing: z.number().optional(),
  reading: z.number().optional(),
  speaking: z.number().optional(),
  overall: z.number().optional(),
  exam_type: z.string().trim().optional(),
  exam_date: z.string().trim().optional(),
});

const testScoresReqSchema = z.object({
  verbal_score: z.number(),
  verbal_rank: z.number(),
  quantitative_score: z.number(),
  quantitative_rank: z.number(),
  analytical_writing_score: z.number().optional(),
  analytical_writing_rank: z.number().optional(),
  data_insights_score: z.number().optional(),
  data_insights_rank: z.number().optional(),
  integrated_reasoning_score: z.number().optional(),
  integrated_reasoning_rank: z.number().optional(),
  total_score: z.number(),
  total_rank: z.number(),
  exam_date: z.string().trim(),
});

const testGreScoresReqSchema = z.object({
  verbal_score: z.number(),
  verbal_rank: z.number(),
  quantitative_score: z.number(),
  quantitative_rank: z.number(),
  analytical_writing_score: z.number(),
  analytical_writing_rank: z.number(),
  exam_date: z.string().trim(),
});

const competitiveTestScoresReqSchema = z
  .object({
    GRE: testGreScoresReqSchema.optional(),
    GMAT: testScoresReqSchema.optional(),
  })
  .optional();

const educationSummaryReqSchema = z.object({
  english_exam_result: englishExamResultReqSchema,
  competitive_test_results: competitiveTestScoresReqSchema,
});

export const CreateLoanApplicationV2RequestSchema = z.object({
  student_personal_information: studentPersonalInformationReqSchema,
  destination_information: destinationInformationReqSchema,
  education_summary: educationSummaryReqSchema,
  loan_amount: z.number(),
  amount_currency: z.string().trim(),
  privacy_consent: z.boolean(),
  terms_and_conditions_consent: z.boolean(),
  supporting_documents: loanDocumentSchema,
  rpId: z.string().trim().optional(),
  source: z.string().trim().optional(),
});

export type CreateLoanApplicationV2Request = z.infer<typeof CreateLoanApplicationV2RequestSchema>;

export const CreateLoanPartialApplicationV2RequestSchema = z.object({
  student_personal_information: studentPersonalInformationReqSchema,
  destination_information: destinationInformationLeadReqSchema,
  privacy_consent: z.boolean(),
  terms_and_conditions_consent: z.boolean(),
  rpId: z.string().trim().optional(),
  source: z.string().trim().optional(),
});

export type CreateLoanPartialApplicationV2Request = z.infer<
  typeof CreateLoanPartialApplicationV2RequestSchema
>;

/**check dates for ISO standard
 * english test
 * intake can be optional since its "legacy and deprecated", default if Jan
 */

const studentPersonalInformation = z
  .object({
    given_name: z.string().optional(),
    last_name: z.string().nullish(), // Nullish covers field not being present, null and undefined
    email_address: z.string().optional(),
    phone_number: z.string().optional(),
    nationality: z.string().optional(),
  })
  .optional();

const destinationInformation = z
  .object({
    destination_country: z.string().nullish(),
    field_of_study: z.string().nullish(),
    school_name: z.string().nullish(),
    program_name: z.string().nullish(),
    expected_arrival_date: z.string().nullish(),
    expected_graduation_date: z.string().nullish(),
  })
  .optional();

const englishExamResult = z
  .object({
    listening: z.number().optional(),
    writing: z.number().optional(),
    reading: z.number().optional(),
    speaking: z.number().optional(),
    overall: z.number().optional(),
    exam_type: z.string().optional(),
    exam_date: z.string().optional(), // assuming ISO 8601 date format as a string
  })
  .optional();

const testResult = z
  .object({
    verbal_score: z.number().optional(),
    verbal_rank: z.number().optional(),
    quantitative_score: z.number().optional(),
    quantitative_rank: z.number().optional(),
    analytical_writing_score: z.number().optional(),
    analytical_writing_rank: z.number().optional(),
    total_score: z.number().optional(),
    total_rank: z.number().optional(),
    exam_date: z.string().optional(), // assuming ISO 8601 date format as a string
  })
  .optional();

const competitiveTestResults = z
  .object({
    GRE: testResult,
    GMAT: testResult,
  })
  .optional();

const educationSummary = z
  .object({
    english_exam_result: englishExamResult,
    competitive_test_results: competitiveTestResults,
  })
  .optional();

const attributes = z
  .object({
    rpId: z.string().nullish(),
    source: z.string().trim().optional(),
    loan_amount: z.number().optional(),
    amount_currency: z.string().optional(),
    privacy_consent: z.boolean().optional(),
    terms_and_conditions_consent: z.boolean().optional(),
    student_personal_information: studentPersonalInformation,
    follow_up_template_id: z.string().optional(),
    destination_information: destinationInformation,
    education_summary: educationSummary,
  })
  .optional();

export const LoanApplicationV2ResponseSchema = z.object({
  data: z.object({ id: z.string(), type: z.string(), attributes: attributes }),
});

export const CreateLoanPartialApplicationV2ResponseSchema = z.object({
  data: z.object({ id: z.string(), type: z.string(), attributes: attributes }),
});

export type LoanApplicationV2Response = z.infer<typeof LoanApplicationV2ResponseSchema>;

export type CreateLoanPartialApplicationV2Response = z.infer<
  typeof CreateLoanPartialApplicationV2ResponseSchema
>;

export function useCreateLoanApplicationV2() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: CreateLoanApplicationV2Request) => {
      const parsedRequest: CreateLoanApplicationV2Request =
        CreateLoanApplicationV2RequestSchema.parse(inputBody);
      const token = await getAccessToken(false);

      return request(V2_LOAN_APPLICATION_URL, {
        body: parsedRequest,
        method: 'POST',
        authorization: token ? `Bearer ${token}` : undefined,
        schema: LoanApplicationV2ResponseSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new LoansError(jsonMessage, 'Create Loan Application Failed');
        },
      });
    },
  );

  const createLoanApplication = (
    params: CreateLoanApplicationV2Request,
    onSuccess?: (result: LoanApplicationV2Response) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: async (response: LoanApplicationV2Response) => {
        queryClient.invalidateQueries([`${V2_LOAN_APPLICATION_URL}_${response?.data?.id}`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    createLoanApplication,
    isCreatingLoanApplication: isLoading,
    createLoanApplicationIsError: isError,
  };
}

export function useLoanApplicationSubmissionV2() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: CreateLoanApplicationV2Request) => {
      const parsedRequest: CreateLoanApplicationV2Request =
        CreateLoanApplicationV2RequestSchema.parse(inputBody);

      return request(V2_LOAN_APPLICATION_SUBMISSION_URL, {
        body: parsedRequest,
        method: 'POST',
        schema: LoanApplicationV2ResponseSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new LoansError(jsonMessage, 'Create Loan Application V3 Failed');
        },
      });
    },
  );

  const loanApplicationSubmission = (
    params: CreateLoanApplicationV2Request,
    onSuccess?: (result: LoanApplicationV2Response) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: async (response: LoanApplicationV2Response) => {
        queryClient.invalidateQueries([`${V2_LOAN_APPLICATION_URL}_${response?.data?.id}`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    loanApplicationSubmission,
    isLoanApplicationSubmitting: isLoading,
    loanApplicationSubmissionIsError: isError,
  };
}

export function useCreateLoanPartialApplicationV2() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: CreateLoanPartialApplicationV2Request) => {
      const parsedRequest: CreateLoanPartialApplicationV2Request =
        CreateLoanPartialApplicationV2RequestSchema.parse(inputBody);
      const token = await getAccessToken(false);

      return request(V2_LEAD_LOAN_URL, {
        body: parsedRequest,
        method: 'POST',
        authorization: token ? `Bearer ${token}` : undefined,
        schema: CreateLoanPartialApplicationV2ResponseSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new LoansError(jsonMessage, 'Create Loan Partial Application Failed');
        },
      });
    },
  );

  const createLoanPartialApplication = (
    params: CreateLoanPartialApplicationV2Request,
    onSuccess?: (result: LoanApplicationV2Response) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: async (response: CreateLoanPartialApplicationV2Response) => {
        queryClient.invalidateQueries([`${V2_LEAD_LOAN_URL}_${response?.data?.id}`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    createLoanPartialApplication,
    isCreatingPartialLoanApplication: isLoading,
    createLoanPartialApplicationIsError: isError,
  };
}

export type EducationSummaryReq = z.infer<typeof educationSummaryReqSchema>;
export function educationSummaryValidation(education_summary: EducationSummaryReq): boolean {
  try {
    educationSummaryReqSchema.parse(education_summary);
  } catch (error) {
    console.error('Education Summary Invalid', error);
    return false;
  }
  return true;
}

const checkDuplicateReqSchema = z.object({
  email: z.string().trim(),
});

export type CheckDuplicateV2Request = z.infer<typeof checkDuplicateReqSchema>;

export const checkDuplicateResponseSchema = z.object({
  data: z.object({
    type: z.string(),
    attributes: z.object({
      is_duplicate_application: z.boolean(),
    }),
  }),
});
export type CheckDuplicateResponse = z.infer<typeof checkDuplicateResponseSchema>;

export function useCheckDuplicateLoanApplication() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(async (inputBody: CheckDuplicateV2Request) => {
    const parsedRequest: CheckDuplicateV2Request = checkDuplicateReqSchema.parse(inputBody);

    return request(V2_CHECK_DUPLICATE_LOAN_APPLICATION_URL, {
      body: parsedRequest,
      method: 'POST',
      schema: checkDuplicateResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new LoansError(jsonMessage, 'Check duplicate failed.');
      },
    });
  });

  const checkDuplicate = (
    params: CheckDuplicateV2Request,
    onSuccess?: (result: CheckDuplicateResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: async (response: CheckDuplicateResponse) => {
        queryClient.invalidateQueries([`${V2_CHECK_DUPLICATE_LOAN_APPLICATION_URL}`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    checkDuplicate,
    isCheckDuplicateLoading: isLoading,
    checkDuplicateError: isError,
  };
}

export function useGetLoan(loanApplicationId: string) {
  const { data, isFetching, isFetched, error, refetch } = useQuery(['get_loan'], async () => {
    return request<LoanApplicationV2Response>(`${V2_LEAD_LOAN_URL}/${loanApplicationId}`, {
      schema: LoanApplicationV2ResponseSchema,
    });
  });

  return {
    loan: data,
    loanError: error,
    refetchLoan: refetch as refetchType,
    isFetchingLoan: isFetching,
    isFetchedLoan: isFetched,
  };
}

export function useGetMinimalLoan(loanApplicationId: string) {
  const { data, isFetching, isFetched, error, refetch } = useQuery(
    ['get_minimal_loan'],
    async () => {
      return request<LoanApplicationV2Response>(
        `${V2_LEAD_LOAN_URL}/${loanApplicationId}/minimal`,
        {
          schema: LoanApplicationV2ResponseSchema,
        },
      );
    },
  );

  return {
    loan: data,
    loanError: error,
    refetchLoan: refetch as refetchType,
    isFetchingLoan: isFetching,
    isFetchedLoan: isFetched,
  };
}

export const SetLoanTemplateReqSchema = z.object({
  follow_up_template_id: z.string(),
});

export type SetLoanTemplateReqBody = z.infer<typeof SetLoanTemplateReqSchema>;

export function useSetLoanTemplate(applicationId: string) {
  const { mutate, isLoading, isError } = useMutation(async (inputBody: SetLoanTemplateReqBody) => {
    return request(V2_LEAD_LOAN_URL + `/${applicationId}`, {
      body: inputBody,
      method: 'PATCH',
      schema: LoanApplicationV2ResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new VasError(jsonMessage, jsonMessage.message);
      },
    });
  });

  const setLoanTemplate = (
    params: SetLoanTemplateReqBody,
    onSuccess?: (data: LoanApplicationV2Response) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (response: LoanApplicationV2Response) => {
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    setLoanTemplate,
    isSettingLoanTemplate: isLoading,
    setLoanTemplateError: isError,
  };
}

export function useSendFollowUpEmail(): [(id: string) => Promise<void>, boolean] {
  const [isFollowUpEmailSending, setIsFollowUpEmailSending] = useState(false);

  const sendFollowUpEmail = async (id: string) => {
    setIsFollowUpEmailSending(true);
    try {
      const response = await fetch(V2_SEND_FOLLOW_UP_EMAIL_URL(id), {
        method: 'POST',
        redirect: 'follow',
      });

      if (!response.ok) {
        const jsonBody = await response.json();
        throw new LoansError(jsonBody, 'Failed to send the follow-up email');
      }
    } finally {
      setIsFollowUpEmailSending(false);
    }
  };

  return [sendFollowUpEmail, isFollowUpEmailSending];
}

const loanAttributes = z.object({
  presigned_url: z.string(),
});

export const FetchLoanByIdResponseSchema = z.object({
  data: z.object({
    id: z.string().optional(),
    type: z.string(),
    attributes: loanAttributes,
  }),
});
export type LoanResponse = z.infer<typeof FetchLoanByIdResponseSchema>;

export const fetchSupportingDocument = async (
  id: string,
  documentName: string,
  fileName: string,
): Promise<LoanResponse> => {
  const url = `${V2_LEAD_LOAN_URL}/${id}/documents/${documentName}/${fileName}/signed_url`;
  const response = await request(url, {
    method: 'GET',
    schema: FetchLoanByIdResponseSchema,
    throwError: async (response: Response) => {
      const jsonMessage = await response.json();
      throw new LoansError(jsonMessage, 'Error fetching document signed URL');
    },
  });

  return response;
};

const documentFileSchema = z.object({
  name: z.string(),
});

const documentSchema = z.object({
  type: z.string(),
  status: z.string().nullable().optional(),
  files: z.array(documentFileSchema),
});

export const FetchDocumentsResponseSchema = z.object({
  documents: z.array(documentSchema),
});

export type DocumentsResponse = z.infer<typeof FetchDocumentsResponseSchema>;

export type FileType = {
  name: string;
};

export type Document = {
  type: string;
  status?: string | null;
  files: FileType[];
};

export function useGetLoanDocuments(loanApplicationId: string) {
  const { data, isFetching, error, refetch, isFetched } = useQuery(
    ['get_loan_documents', loanApplicationId],
    async () => {
      return request<DocumentsResponse>(`${V2_LEAD_LOAN_URL}/${loanApplicationId}/documents`, {
        schema: FetchDocumentsResponseSchema,
      });
    },
  );

  return {
    documents: data?.documents,
    loanDocumentsError: error,
    refetchLoanDocuments: refetch as refetchType,
    isFetchingLoanDocuments: isFetching,
    isFetchedLoanDocuments: isFetched,
  };
}

export const UpdateUserDocumentStatusSchema = z.object({
  documents: z.array(
    z.object({
      type: z.string(),
      status: z.string(),
    }),
  ),
});

export type UpdateUserDocumentStatusReq = z.infer<typeof UpdateUserDocumentStatusSchema>;
export type UpdateUserDocumentStatusResp = z.infer<typeof UpdateUserDocumentStatusSchema>;

export function useUserUpdateDocumentStatus(applicationId: string) {
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: UpdateUserDocumentStatusReq) => {
      return request(V2_LEAD_LOAN_URL + `/${applicationId}/documents/user`, {
        body: inputBody,
        method: 'PATCH',
        schema: UpdateUserDocumentStatusSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new VasError(jsonMessage, jsonMessage.message);
        },
      });
    },
  );

  const updateUserDocumentStatus = (
    params: UpdateUserDocumentStatusReq,
    onSuccess?: (data: UpdateUserDocumentStatusResp) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: (response: UpdateUserDocumentStatusResp) => {
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };
  return {
    updateUserDocumentStatus,
    isUpdatingDocumentStatus: isLoading,
    updateDocumentStatusError: isError,
  };
}

const UpdateDocumentsReqBodySchema = z.object({
  documents: z.array(
    z.object({
      type: z.string(),
      status: z.string(),
    }),
  ),
});

const UpdateDocumentsResponseSchema = z.object({
  documents: z.array(
    z.object({
      type: z.string(),
      status: z.string().optional(),
      files: z
        .array(
          z.object({
            name: z.string(),
          }),
        )
        .optional(),
    }),
  ),
});

type UpdateDocumentsReqBody = z.infer<typeof UpdateDocumentsReqBodySchema>;
type UpdateDocumentsResponse = z.infer<typeof UpdateDocumentsResponseSchema>;

export function useUpdateDocuments(applicationId: string) {
  const { mutate, isLoading, isError, error, data } = useMutation<
    UpdateDocumentsResponse,
    unknown,
    UpdateDocumentsReqBody
  >(async (inputBody: UpdateDocumentsReqBody) => {
    return request(`${V2_LEAD_LOAN_URL}/${applicationId}/documents`, {
      body: inputBody,
      method: 'PATCH',
      schema: UpdateDocumentsResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new LoansError(jsonMessage, jsonMessage.message);
      },
    });
  });

  const updateDocuments = (
    params: UpdateDocumentsReqBody,
    onSuccess?: (data: UpdateDocumentsResponse) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: response => onSuccess?.(response),
      onError: error => onError?.(error),
    });
  };

  return {
    updateDocuments,
    isUpdatingDocuments: isLoading,
    updateDocumentsError: isError ? error : null,
    updatedDocuments: data,
  };
}

export const V2_SEND_SUBMISSION_EMAIL_TO_PROVIDER_URL = (applicationId: string) =>
  `${V2_LEAD_LOAN_URL}/${applicationId}/submission`;

const sendSubmissionEmailRequestSchema = z
  .object({
    bank_email: z.string().email().optional(),
  })
  .nullable();

export function useSendSubmissionEmail() {
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError } = useMutation(
    async ({ applicationId, body }: { applicationId: string; body?: { bank_email?: string } }) => {
      const parsedBody = sendSubmissionEmailRequestSchema.parse(body);
      return request(V2_SEND_SUBMISSION_EMAIL_TO_PROVIDER_URL(applicationId), {
        body: parsedBody,
        method: 'POST',
        schema: sendSubmissionEmailRequestSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new LoansError(jsonMessage, 'Failed to send submission email.');
        },
      });
    },
  );

  const sendSubmissionEmail = (
    applicationId: string,
    body?: { bank_email?: string },
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(
      { applicationId, body },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([V2_SEND_SUBMISSION_EMAIL_TO_PROVIDER_URL(applicationId)]);
          onSuccess?.();
        },
        onError: error => {
          onError?.(error);
        },
      },
    );
  };

  return {
    sendSubmissionEmail,
    isSendSubmissionEmailLoading: isLoading,
    sendSubmissionEmailError: isError,
  };
}

export const V2_SET_READY_FOR_REVIEW_URL = (applicationId: string) =>
  `${V2_LEAD_LOAN_URL}/${applicationId}/ready_for_review`;

export function useSetReadyForReview() {
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError } = useMutation(async (applicationId: string) => {
    return request(V2_SET_READY_FOR_REVIEW_URL(applicationId), {
      method: 'POST',
      //TODO: Not tested, probably correct
      schema: LoanApplicationV2ResponseSchema,
      throwError: async (response: Response) => {
        const jsonMessage = await response.json();
        throw new LoansError(jsonMessage, 'Failed to set ready for review.');
      },
    });
  });

  const setReadyForReview = (
    applicationId: string,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(applicationId, {
      onSuccess: () => {
        queryClient.invalidateQueries([V2_SET_READY_FOR_REVIEW_URL(applicationId)]);
        onSuccess?.();
      },
      onError: error => {
        onError?.(error);
      },
    });
  };

  return {
    setReadyForReview,
    isSetReadyForReviewLoading: isLoading,
    setReadyForReviewError: isError,
  };
}

export const deleteFile = async (
  id: string,
  documentName: string,
  fileName: string,
): Promise<void> => {
  const url = `${V2_LEAD_LOAN_URL}/${id}/documents/${documentName}/${fileName}`;
  await request(url, {
    method: 'DELETE',
    schema: z.unknown(),
    throwError: async (response: Response) => {
      const jsonMessage = await response.json();
      throw new LoansError(jsonMessage, 'Error fetching document signed URL');
    },
  });
  return;
};

const SupporteSchoolResponsSchema = z.object({ data: z.array(z.string()) });

export function useGetLoanSupportedSchools() {
  const { data, isFetching, error, isLoading } = useQuery(['loan_supported_schools'], async () => {
    return request<{ data: string[] }>(`${V2_LOAN_APPLICATION_URL}/supported_schools`, {
      schema: SupporteSchoolResponsSchema,
      allowAnonymous: true,
    });
  });

  return {
    supportedSchools: data?.data || [],
    supportedSchoolsError: error,
    isFetchingSupportedSchools: isFetching,
    isLoadingSupportedSchools: isLoading,
  };
}

const SupportedProgramsResponseSchema = z.object({ data: z.array(z.string()) });

export function useGetSchoolPrograms(schoolName?: string) {
  const { data, isFetching, error, isLoading } = useQuery(
    ['school_programs', schoolName],
    async () => {
      if (!schoolName) {
        return { data: [] };
      }

      return request<{ data: string[] }>(
        `${V2_LOAN_APPLICATION_URL}/supported_programs/${encodeURIComponent(schoolName)}`,
        {
          schema: SupportedProgramsResponseSchema,
          allowAnonymous: true,
        },
      );
    },
    {
      enabled: !!schoolName, // Only run query when schoolName is provided
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes (session-based caching)
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    },
  );

  return {
    programs: data?.data || [],
    programsError: error,
    isFetchingPrograms: isFetching,
    isLoadingPrograms: isLoading,
  };
}
